import { Fragment } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ArrowRight,
  Check,
  Clock,
  Landmark,
  Link2,
  Receipt,
  ShieldCheck,
  UserCheck,
  Wallet,
} from 'lucide-react';
import { Badge, Button, Card, CardContent } from '@caratuva/ui';
import { Section, SectionHeading } from '../components/Section.tsx';
import { GETPAID_URL } from '../lib/links.ts';
import { useInView } from '../lib/useInView.ts';
import { useRouteMeta } from '../lib/useDocumentMeta.ts';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

type IconType = typeof Wallet;

function FeatureCard({
  icon: Icon,
  titleId,
  bodyId,
}: {
  icon: IconType;
  titleId: string;
  bodyId: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-[var(--color-navy-50)] text-[var(--color-navy-600)]">
          <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
        </div>
        <h3 className="text-base font-semibold text-[var(--color-navy-950)]">
          <FormattedMessage id={titleId} />
        </h3>
        <p className="mt-2 text-sm text-[var(--color-navy-700)]">
          <FormattedMessage id={bodyId} />
        </p>
      </CardContent>
    </Card>
  );
}

// Animated mountain/sunrise hero — the brand's Pico Caratuva ridge motif in
// motion. Decorative scene is aria-hidden; the copy is the accessible content.
// Animation + reduced-motion handling live in index.css (.chero-*).
function HeroScene() {
  return (
    <svg
      className="chero-scene"
      viewBox="0 0 1200 630"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="cheroGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#f7d873" />
          <stop offset="0.5" stopColor="#f2c744" stopOpacity="0.55" />
          <stop offset="1" stopColor="#f2c744" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cheroSweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f7d873" stopOpacity="0" />
          <stop offset="0.5" stopColor="#f7d873" stopOpacity="0.85" />
          <stop offset="1" stopColor="#f7d873" stopOpacity="0" />
        </linearGradient>
        <clipPath id="cheroNearClip">
          <path d="M0 560 L180 470 L320 520 L470 430 L640 540 L760 410 L900 540 L1050 470 L1200 540 L1200 630 L0 630 Z" />
        </clipPath>
      </defs>

      <g className="chero-sun-group">
        <circle className="chero-sun-glow" cx="930" cy="250" r="210" fill="url(#cheroGlow)" />
        <circle cx="930" cy="250" r="66" fill="#f2c744" />
      </g>

      <path
        className="chero-ridge-far"
        d="M0 470 L150 392 L300 446 L470 360 L640 452 L820 372 L1000 452 L1200 392 L1200 630 L0 630 Z"
        fill="#0a3a28"
      />

      <g className="chero-ridge-near">
        <path
          d="M0 560 L180 470 L320 520 L470 430 L640 540 L760 410 L900 540 L1050 470 L1200 540 L1200 630 L0 630 Z"
          fill="#00a868"
        />
        <path
          d="M0 560 L180 470 L320 520 L470 430 L640 540 L760 410 L900 540 L1050 470 L1200 540"
          fill="none"
          stroke="#0b2018"
          strokeWidth="3"
          strokeLinejoin="round"
          opacity="0.35"
        />
        <g clipPath="url(#cheroNearClip)">
          <rect
            className="chero-sweep"
            x="-360"
            y="400"
            width="360"
            height="240"
            fill="url(#cheroSweep)"
          />
        </g>
      </g>
    </svg>
  );
}

function Hero() {
  const intl = useIntl();
  return (
    <section
      className="chero relative w-full overflow-hidden"
      aria-label={intl.formatMessage({ id: 'home.hero.headline' })}
    >
      <HeroScene />
      <div className="relative z-10 mx-auto flex min-h-[88vh] max-w-5xl flex-col justify-center px-6 py-24">
        <p className="chero-rise chero-d1 mb-5 inline-block w-fit rounded-full border border-[rgba(0,168,104,0.3)] bg-[rgba(0,168,104,0.14)] px-3 py-1 text-sm font-medium text-[var(--color-navy-100)]">
          <FormattedMessage id="home.hero.eyebrow" />
        </p>
        <h1
          className="chero-rise chero-d2 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight text-[var(--color-beige-50)] sm:text-6xl"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          <FormattedMessage id="home.hero.headline" />
        </h1>
        <p className="chero-rise chero-d3 mt-6 max-w-2xl text-lg text-[var(--color-navy-100)]">
          <FormattedMessage id="home.hero.subhead" />
        </p>
        <div className="chero-rise chero-d4 mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button
            asChild
            size="lg"
            className="bg-[var(--color-gold-500)] text-[var(--color-navy-950)] hover:bg-[var(--color-gold-300)]"
          >
            <a href={GETPAID_URL}>{intl.formatMessage({ id: 'home.hero.cta.primary' })}</a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-[rgba(251,248,239,0.5)] bg-[rgba(251,248,239,0.1)] backdrop-blur-sm text-[var(--color-beige-50)] hover:bg-[rgba(251,248,239,0.2)] hover:text-[var(--color-beige-50)]"
          >
            <Link to="/integrators">{intl.formatMessage({ id: 'home.hero.cta.secondary' })}</Link>
          </Button>
        </div>
        <p className="chero-rise chero-d4 mt-4 text-sm text-[var(--color-navy-200)]">
          <FormattedMessage id="home.hero.note" />
        </p>
      </div>
    </section>
  );
}

// The problem sellers/freelancers/exporters feel with traditional international
// payout platforms — qualitative, no invented competitor numbers.
function Problem() {
  return (
    <Section tint="canvas">
      <SectionHeading
        title={<FormattedMessage id="home.problem.title" />}
        subtitle={<FormattedMessage id="home.problem.subtitle" />}
      />
      <div className="mt-12 grid gap-6 sm:grid-cols-3">
        <FeatureCard
          icon={Clock}
          titleId="home.problem.slow.title"
          bodyId="home.problem.slow.body"
        />
        <FeatureCard
          icon={Wallet}
          titleId="home.problem.spread.title"
          bodyId="home.problem.spread.body"
        />
        <FeatureCard
          icon={Landmark}
          titleId="home.problem.parked.title"
          bodyId="home.problem.parked.body"
        />
      </div>
    </Section>
  );
}

function HowItWorks() {
  const steps = [
    { n: '1', icon: UserCheck, t: 'home.how.step1.title', b: 'home.how.step1.body' },
    { n: '2', icon: ShieldCheck, t: 'home.how.step2.title', b: 'home.how.step2.body' },
    { n: '3', icon: Link2, t: 'home.how.step3.title', b: 'home.how.step3.body' },
    { n: '4', icon: Wallet, t: 'home.how.step4.title', b: 'home.how.step4.body' },
  ];
  return (
    <Section tint="white">
      <SectionHeading
        title={<FormattedMessage id="home.how.title" />}
        subtitle={<FormattedMessage id="home.how.subtitle" />}
      />
      <ol className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s) => (
          <li key={s.n} className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-navy-600)] text-lg font-semibold text-[var(--color-beige-50)]">
              {s.n}
            </div>
            <h3 className="mt-4 text-base font-semibold text-[var(--color-navy-950)]">
              <FormattedMessage id={s.t} />
            </h3>
            <p className="mt-2 text-sm text-[var(--color-navy-700)]">
              <FormattedMessage id={s.b} />
            </p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

// Why Caratuva — trust section (transparent fee, compliance, no foreign
// account, money lands in your bank).
function Why() {
  return (
    <Section tint="canvas">
      <SectionHeading
        title={<FormattedMessage id="home.why.title" />}
        subtitle={<FormattedMessage id="home.why.subtitle" />}
      />
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <FeatureCard icon={Receipt} titleId="home.why.fee.title" bodyId="home.why.fee.body" />
        <FeatureCard icon={Landmark} titleId="home.why.pix.title" bodyId="home.why.pix.body" />
        <FeatureCard
          icon={ShieldCheck}
          titleId="home.why.compliance.title"
          bodyId="home.why.compliance.body"
        />
        <FeatureCard
          icon={Wallet}
          titleId="home.why.noforeign.title"
          bodyId="home.why.noforeign.body"
        />
      </div>
    </Section>
  );
}

// Qualitative comparison vs. traditional international payout platforms. Model,
// not numbers — no invented competitor fees.
function Compare() {
  const { ref, inView } = useInView<HTMLDivElement>();
  const rows = [1, 2, 3, 4];
  return (
    <Section tint="white">
      <SectionHeading
        title={<FormattedMessage id="home.compare.title" />}
        subtitle={<FormattedMessage id="home.compare.subtitle" />}
      />
      <div
        ref={ref}
        className={`cflow mx-auto mt-12 grid max-w-4xl overflow-hidden rounded-xl border border-[var(--color-navy-200)] sm:grid-cols-2${
          inView ? ' in-view' : ''
        }`}
      >
        <div className="cflow-head cflow-before border-b border-[var(--color-navy-200)] bg-[var(--color-navy-50)] px-5 py-3 text-sm font-semibold text-[var(--color-navy-700)]">
          <FormattedMessage id="home.compare.them.label" />
        </div>
        <div className="cflow-head cflow-after border-b border-[var(--color-navy-200)] bg-[rgba(0,168,104,0.12)] px-5 py-3 text-sm font-semibold text-[var(--color-navy-900)]">
          <FormattedMessage id="home.compare.us.label" />
        </div>
        {rows.map((n, i) => (
          <Fragment key={n}>
            <div
              className={`cflow-cell cflow-before cflow-d${
                i + 1
              } border-b border-[var(--color-navy-100)] bg-[var(--color-navy-50)] px-5 py-4 text-sm text-[var(--color-navy-600)]`}
            >
              <FormattedMessage id={`home.compare.row${n}.them`} />
            </div>
            <div
              className={`cflow-cell cflow-after cflow-d${
                i + 1
              } flex gap-2 border-b border-[var(--color-navy-100)] bg-[rgba(0,168,104,0.05)] px-5 py-4 text-sm text-[var(--color-navy-800)]`}
            >
              <Check
                size={18}
                strokeWidth={2}
                className="mt-0.5 shrink-0 text-[var(--color-success)]"
                aria-hidden="true"
              />
              <FormattedMessage id={`home.compare.row${n}.us`} />
            </div>
          </Fragment>
        ))}
      </div>
      <p className="mx-auto mt-4 max-w-4xl text-center text-xs text-[var(--color-navy-500)]">
        <FormattedMessage id="home.compare.note" />
      </p>
    </Section>
  );
}

// Affiliate teaser — social proof / earn-by-referring.
function AffiliateTeaser() {
  const intl = useIntl();
  return (
    <Section tint="canvas">
      <div className="mx-auto max-w-3xl">
        <Card className="border-[var(--color-navy-300)]">
          <CardContent className="flex flex-col items-start gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Badge variant="success">
                <FormattedMessage id="home.affiliate.badge" />
              </Badge>
              <h3 className="mt-3 text-xl font-semibold text-[var(--color-navy-950)]">
                <FormattedMessage id="home.affiliate.title" />
              </h3>
              <p className="mt-2 text-sm text-[var(--color-navy-700)]">
                <FormattedMessage id="home.affiliate.body" />
              </p>
            </div>
            <Button asChild variant="outline" className="shrink-0">
              <Link to="/affiliates">
                {intl.formatMessage({ id: 'home.affiliate.cta' })}
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </Section>
  );
}

function CtaBand() {
  const intl = useIntl();
  return (
    <Section tint="brand">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-beige-50)] sm:text-4xl">
          <FormattedMessage id="home.cta.title" />
        </h2>
        <p className="mt-4 text-lg text-[var(--color-beige-200)]">
          <FormattedMessage id="home.cta.subtitle" />
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild size="lg">
            <a href={GETPAID_URL}>{intl.formatMessage({ id: 'home.cta.primary' })}</a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-[rgba(251,248,239,0.5)] bg-[rgba(251,248,239,0.1)] backdrop-blur-sm text-[var(--color-beige-50)] hover:bg-[rgba(251,248,239,0.2)] hover:text-[var(--color-beige-50)]"
          >
            <Link to="/integrators">{intl.formatMessage({ id: 'home.cta.secondary' })}</Link>
          </Button>
        </div>
      </div>
    </Section>
  );
}

function LandingPage() {
  useRouteMeta('meta.home.title', 'meta.home.description', 'meta.home.social');
  return (
    <>
      <Hero />
      <Problem />
      <HowItWorks />
      <Why />
      <Compare />
      <AffiliateTeaser />
      <CtaBand />
    </>
  );
}
